import { Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { Banner } from './Banner';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { LAYER_LABEL, spendLevel } from './SpendMeter';
import { useBrand } from '../lib/brand';
import { useAppSettings, useSpend } from '../lib/queries/shell';
import { errorMessage } from '../lib/query';
import { findPage } from '../lib/nav';
import { fmtDate } from '../lib/format';

function nextMonthStart(now = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
}

/** Banners that apply to every screen: global pause, a brand's layer budget used up, brand load errors. */
function ShellBanners() {
  const { pathname } = useLocation();
  const { brand, brandsError, role } = useBrand();
  const onBrandPage = !!findPage(pathname)?.brand;
  const settings = useAppSettings().data;
  const spend = useSpend(onBrandPage ? brand : null);
  const over = (spend.layers ?? []).filter((l) => spendLevel(l.spent, l.budget) === 'over');

  return (
    <>
      {brandsError ? <Banner tone="bad">The brand list did not load. {errorMessage(brandsError)}</Banner> : null}
      {settings?.paused ? (
        <Banner tone="warn">
          <b>All scheduled checks are paused.</b> Nothing new is collected until the owner resumes them in Settings.
        </Banner>
      ) : null}
      {onBrandPage && brand
        ? over.map((l) => (
            <Banner
              key={l.layer}
              tone="bad"
              actions={
                role === 'admin' ? (
                  <Link className="btn sm" to="/settings">
                    Change budget
                  </Link>
                ) : null
              }
            >
              <b>{LAYER_LABEL[l.layer]} budget is used up for this month.</b> Scheduled {LAYER_LABEL[l.layer]} checks for{' '}
              {brand.name} are paused until {fmtDate(nextMonthStart())}. Data already collected stays. On-demand calls need an
              admin.
            </Banner>
          ))
        : null}
    </>
  );
}

/** The app frame: sidebar, top bar, shared banners, and the current screen. */
export function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className={`app${menuOpen ? ' menu-open' : ''}`}>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <Sidebar onNavigate={() => setMenuOpen(false)} />
      <div className="side-scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      <div className="col">
        <TopBar onMenu={() => setMenuOpen((o) => !o)} menuOpen={menuOpen} />
        <main id="main" tabIndex={-1}>
          <ShellBanners />
          <RouteErrorBoundary>
            <Suspense
              fallback={
                <div className="loading" role="status">
                  Loading…
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
